import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface OrganizationState {
  activeOrganizationId: string | null;
  setActiveOrganizationId: (id: string | null) => void;
}

// The active organization scopes the whole app (teams, projects, lists, and
// label filters). Persisted so the choice survives reloads; OrgSwitcher
// re-validates it against the fetched org list and falls back to the first
// org when the stored id is stale (e.g. the org was deleted).
export const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set) => ({
      activeOrganizationId: null,
      setActiveOrganizationId: (id) => set({ activeOrganizationId: id }),
    }),
    { name: 'projectflow-active-org' },
  ),
);

/**
 * Keeps the active-org picker honest on pages that navigate straight to a
 * single resource (e.g. a notification linking to /teams/:teamId) rather
 * than browsing within the active org — without this, the picker could keep
 * showing a stale org while the page renders data that actually belongs to
 * a different one.
 *
 * `onDivergedWhileViewing`, when supplied, is called instead of reverting
 * the store when the mismatch was caused by the *store* changing while
 * looking at the *same* entity — i.e. the user picked a different org in
 * OrgSwitcher while viewing this project/team/list. Mounting on (or
 * navigating to) a different entity still syncs the store TO that entity
 * unchanged, whether or not a callback is supplied.
 */
export function useSyncActiveOrganization(
  organizationId: string | undefined,
  onDivergedWhileViewing?: () => void,
) {
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId);
  const setActiveOrganizationId = useOrganizationStore((s) => s.setActiveOrganizationId);

  // Latest-callback ref so passing a fresh inline arrow each render doesn't
  // retrigger the effect below — only organizationId/activeOrganizationId should.
  const onDivergedRef = useRef(onDivergedWhileViewing);
  onDivergedRef.current = onDivergedWhileViewing;

  // Tracks the entity's own org id across renders so a mismatch caused by
  // the ENTITY changing (follow it) can be told apart from the STORE
  // changing while the entity stayed put (bounce away instead). Starts at
  // `undefined` regardless of the first-render organizationId — never a
  // real org id — so a genuine first mount (including one that's already
  // mismatched, e.g. a deep link into a different org) is always treated
  // as "entity changed" and synced, never mistaken for a diverged store.
  const prevOrganizationIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!organizationId) return;

    const entityChanged = prevOrganizationIdRef.current !== organizationId;
    prevOrganizationIdRef.current = organizationId;

    if (organizationId === activeOrganizationId) return;

    if (entityChanged || !onDivergedRef.current) {
      setActiveOrganizationId(organizationId);
    } else {
      onDivergedRef.current();
    }
  }, [organizationId, activeOrganizationId, setActiveOrganizationId]);
}
